/**
 * TickGroup — pick several from a short closed list: a fieldset whose
 * legend names the group, then one wrapping row of labelled checkboxes.
 *
 * Each label is the whole tap target, at least 2.75rem tall, and each
 * checkbox is its own tab stop (checkboxes, never a radiogroup or a list
 * box). The row wraps and never scrolls sideways. `onChange` returns the
 * ticked values in `options` order, so a stored set is canonical however
 * the ticks were pressed.
 */

import { type ReactNode } from 'react';
import { FLD_LABEL } from '@/components/primitives/modalFormStyles';

export interface TickGroupProps {
  /** The group's id: the legend is `${id}-label`, each box `${id}-${value}`. */
  id: string;
  /** The words the legend reads. */
  legend: string;
  options: readonly { value: string; label: string }[];
  /** The ticked values. */
  value: readonly string[];
  /** The ticked values after a press, in `options` order. */
  onChange(next: string[]): void;
  /** The group's testid; each box is `${testIdPrefix}-${value}`. */
  testIdPrefix: string;
  describedBy?: string;
}

export function TickGroup({
  id,
  legend,
  options,
  value,
  onChange,
  testIdPrefix,
  describedBy,
}: TickGroupProps): ReactNode {
  const ticked = new Set(value);
  const press = (v: string): void => {
    const next = new Set(ticked);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(options.map((o) => o.value).filter((o) => next.has(o)));
  };
  return (
    <fieldset data-testid={testIdPrefix} aria-describedby={describedBy} className="m-0 min-w-0 border-0 p-0">
      <legend id={`${id}-label`} className={FLD_LABEL}>
        {legend}
      </legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map((o) => (
          <label
            key={o.value}
            className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-[0.9375rem] text-sage-800"
          >
            <input
              type="checkbox"
              id={`${id}-${o.value}`}
              data-testid={`${testIdPrefix}-${o.value}`}
              className="h-5 w-5 accent-sage-700"
              checked={ticked.has(o.value)}
              onChange={() => press(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
